<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import AcceptTerms from '@/Components/AcceptTerms.vue';
import { Head, router } from '@inertiajs/vue3';
import PhoneInput from '@/Components/PhoneInput.vue';
import Swal from 'sweetalert2';
</script>

<script>
export default {
    props: {
        countries: {
            type: Array
        },
        errors: {
            type: Object
        }
    },
    data() {
        return {
            userData: {
                name: '',
                email: '',
                phone: '',
                username: '',
                password: '',
                passwordRepeat: '',
                termsAccepted: false,
                invoiceData: {
                    privatePerson: false,
                    name: '',
                    country: '',
                    postcode: '',
                    city: '',
                    address: '',
                    vatNr: '',
                },
            }
        }
    },
    methods: {

        emailChanged() {
            if (!this.userData.username) {
                this.userData.username = this.userData.email;
            }
        },

        registerUser() {
            router.post(route('b2bRegister'), {
                name: this.userData.name,
                email: this.userData.email,
                phone: this.userData.phone,
                username: this.userData.username,
                password: this.userData.password,
                passwordRepeat: this.userData.passwordRepeat,
                termsAccepted: this.userData.termsAccepted,
                invoiceData: this.userData.invoiceData,
                redirect: true
            }, {
                preserveScroll: (page) => Object.keys(page.props.errors).length,
                onSuccess: () => {
                    if (dataLayer) {
                        dataLayer.push({
                            event: 'registration',
                            email: this.userData.email,
                            phone: this.userData.phone,
                            country: this.userData.invoiceData.country,
                            city: this.userData.invoiceData.city,
                            postcode: this.userData.invoiceData.postcode,
                        });
                    }
                },
                onError: errors => {
                    if (errors.alert) {
                        Swal.fire({
                            icon: 'error',
                            text: errors.alert,
                        });
                    } else {
                        this.$utils.scrollToFirstError();
                    }
                },
            });
        },

        cityFromPostcode() {
            if (!this.userData.invoiceData.postcode || this.userData.invoiceData.city) {
                return;
            }

            const that = this;

            axios.get(route('cityFromPostcode'), {
                params: {
                    postcode: that.userData.invoiceData.postcode
                }
            }).then(function (response) {
                if (response.data?.city) {
                    that.userData.invoiceData.city = response.data.city;
                }
            });
        },

        postcodeFromCity() {
            if (!this.userData.invoiceData.city || this.userData.invoiceData.postcode) {
                return;
            }

            const that = this;

            axios.get(route('postcodeFromCity'), {
                params: {
                    city: that.userData.invoiceData.city
                }
            }).then(function (response) {
                if (response.data?.postcode) {
                    that.userData.invoiceData.postcode = response.data.postcode;
                }
            });
        },

    },
    mounted() {

        // Page view DataLayer event
        if (dataLayer) {
            dataLayer.push({
                event: 'virtualPageView',
                page_type: 'other',
                site_language: this.$page.props.locale?.toUpperCase() || '?',
                login_status: this.$page.props.user ? 'logged_in' : 'logged_out',
                user_id: this.$page.props.user ? this.$page.props.user.id : null,
            });
        }

    }
}
</script>

<template>

    <Head :title="$t('B2B regisztráció')" />

    <AppLayout>

        <header class="header">
            <div class="header-body header-body-subpage">
                <div class="container-fluid">
                    <div class="row align-items-center justify-content-center">
                        <div class="col-12 col-sm-auto col-lg text-center text-sm-right text-lg-left">
                            <h1 class="header-h1">{{ $t('B2B regisztráció') }}</h1>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <div class="header-placeholder"></div>

        <div class="subpage-content">
            <div class="container subpage-container">
                <div class="row">
                    <main class="main col-12">
                        <section>
                            <div class="row section-title d-none">
                                <div class="col-12">
                                    <h2>{{ $t('B2B regisztráció') }}</h2>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <form @submit.prevent="registerUser">
                                        <p class="fs-6 text-center" v-html="$t('Ha szeretne csatlakozni a miniBUD üzleti partneri hálózatához, kérjük, töltse ki a regisztrációs űrlapot. Ezt követően a miniBUD repülőtéri transzfert és a további szolgáltatásokat kedvezménnyel foglalhatja majd.')">
                                        </p>
                                        <div v-if="errors.form" class="alert alert-danger fw-bold mb-3" role="alert">{{ errors.form }}</div>
                                        <div class="row">
                                            <div class="col-12">
                                                <div class="col-12">
                                                    <h2 class="my-3">{{ $t('Felhasználói adatok') }}</h2>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="name" :placeholder="$t('Kapcsolattartó neve')" v-model="userData.name" maxlength="150">
                                                    <label for="name">{{ $t('Kapcsolattartó neve') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="email" class="form-control" :class="{ 'is-invalid': errors.email }" id="email" :placeholder="$t('E-mail cím')" aria-labelledby="emailHelpBlock" v-model="userData.email" maxlength="150" @change="emailChanged">
                                                    <label for="email">{{ $t('E-mail cím') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                                                    <div id="emailHelpBlock" class="form-text">
                                                        {{ $t('Erre a címre küldjük majd a foglalásokról szóló visszaigazolást') }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <PhoneInput id="phone" class="form-control d-flex" :class="{ 'is-invalid': errors.phone }" :placeholder="$t('Mobiltelefonszám')" v-model="userData.phone" />
                                                    <label for="phone">{{ $t('Mobiltelefonszám') }}<span class="text-danger">*</span></label>
                                                    <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                                                    <div id="phoneHelpBlock" class="form-text">
                                                        {{ $t('Erre a telefonszámra küldjük majd a foglalásokkal kapcsolatos SMS üzeneteket') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors.username }" id="username" name="username" :placeholder="$t('Felhasználói név')" v-model="userData.username" autocomplete="username">
                                                    <label for="username">{{ $t('Felhasználói név') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.username" class="invalid-feedback">{{ errors.username }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="password" class="form-control" :class="{ 'is-invalid': errors.password }" id="password" name="password" :placeholder="$t('Jelszó')" v-model="userData.password" autocomplete="new-password">
                                                    <label for="password">{{ $t('Jelszó') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="password" class="form-control" :class="{ 'is-invalid': errors.passwordRepeat }" id="passwordRepeat" name="passwordRepeat" :placeholder="$t('Jelszó ismét')" v-model="userData.passwordRepeat" autocomplete="new-password">
                                                    <label for="passwordRepeat">{{ $t('Jelszó ismét') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors.passwordRepeat" class="invalid-feedback">{{ errors.passwordRepeat }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-12">
                                                <h2 class="my-3">{{ $t('Számlázási adatok') }}</h2>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-6">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.name'] }" id="invName" :placeholder="$t(userData.invoiceData.privatePerson ? 'Név' : 'Cégnév')" maxlength="150" v-model="userData.invoiceData.name" />
                                                    <label for="invName">
                                                        <span v-if="userData.invoiceData.privatePerson">{{ $t('Név') }}</span><span v-else>{{ $t('Cégnév') }}</span>
                                                        <span class="text-danger">*</span>
                                                    </label>
                                                    <div v-if="errors['invoiceData.name']" class="invalid-feedback">{{ errors['invoiceData.name'] }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="form-floating mb-3">
                                                    <select class="form-select" :class="{ 'is-invalid': errors['invoiceData.country'] }" name="invCountry" id="invCountry" v-model="userData.invoiceData.country">
                                                        <option v-for="c in countries" :value="$page.props.locale == 'hu' ? c.name_hu : c.name_en">{{
                                                            $page.props.locale == 'hu' ? c.name_hu : c.name_en }}</option>
                                                    </select>
                                                    <label for="invCountry">{{ $t('Ország') }}</label>
                                                    <div v-if="errors['invoiceData.country']" class="invalid-feedback">{{ errors['invoiceData.country'] }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.postcode'] }" id="invPostcode" maxlength="10" :placeholder="$t('Irányítószám')" v-model="userData.invoiceData.postcode" @change="cityFromPostcode" />
                                                    <label for="invPostcode">{{ $t('Irányítószám') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors['invoiceData.postcode']" class="invalid-feedback">{{ errors['invoiceData.postcode'] }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-8">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.city'] }" id="invCity" maxlength="50" :placeholder="$t('Település')" v-model="userData.invoiceData.city" @change="postcodeFromCity" />
                                                    <label for="invCity">{{ $t('Település') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors['invoiceData.city']" class="invalid-feedback">{{ errors['invoiceData.city'] }}</div>
                                                </div>
                                            </div>
                                            <div :class="[userData.invoiceData.privatePerson ? 'col-12' : 'col-md-8']">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.address'] }" id="invAddress" maxlength="150" :placeholder="$t('Cím (utca, házszám)')" v-model="userData.invoiceData.address" />
                                                    <label for="invAddress">{{ $t('Cím (utca, házszám)') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors['invoiceData.address']" class="invalid-feedback">{{ errors['invoiceData.address'] }}</div>
                                                </div>
                                            </div>
                                            <div class="col-md-4" v-if="!userData.invoiceData.privatePerson">
                                                <div class="form-floating mb-3">
                                                    <input type="text" class="form-control" :class="{ 'is-invalid': errors['invoiceData.vatNr'] }" id="invVatNr" maxlength="20" :placeholder="$t('Adószám')" v-model="userData.invoiceData.vatNr" />
                                                    <label for="invVatNr">{{ $t('Adószám') }}<span class="text-danger">*</span></label>
                                                    <div v-if="errors['invoiceData.vatNr']" class="invalid-feedback">{{ errors['invoiceData.vatNr'] }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-12">
                                                <div class="form-check mb-3">
                                                    <AcceptTerms class="form-check-input" :class="{ 'is-invalid': errors.termsAccepted }" id="acceptTerms" v-model="userData.termsAccepted" />
                                                    <div v-if="errors.termsAccepted" class="invalid-feedback">{{ errors.termsAccepted }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="mb-3">
                                            <button type="submit" class="btn btn-primary px-5 text-uppercase">{{ $t('B2B regisztráció') }}</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>

    </AppLayout>
</template>
